/* ============================================================================
 * ez-configurator-hitch.js — the EZ-Snap Hitch Enclosure Kit configurator island (vanilla JS).
 *
 * Ports demo/app/products/hitch-enclosure-kit/page.jsx §1 to a framework-free island that mounts
 * inside sections/ez-configurator-hitch.liquid (contract §1 split for the configurator lane — the
 * SECTION file stays lean; this asset carries the interactivity, like ez-configurator-shade.js).
 *
 * STEP FLOW (5-step peek-preview accordion):
 *   1  Enclosure color  (BK/BG/WH/GY — drives the kit SKU + price)
 *   2  Stud color       (match panel = included, or a different body colour = one flat stud color upgrade;
 *                        the studs ship inside the kit's EZ-C-SNA-{n}-{panel}-{stud} snap pack)
 *   3  Pipe clamps      (optional; pack size 25/50/75/100, recommended pre-checked)
 *   4  Add-ons          (storage bag, extra Gecko Velcro™)
 *   5  Review           (line list, edit/remove, total, multi-line ATC)
 *
 * DATA: the section injects a catalog JSON into window.__EZ_HITCH_CFG__[sectionId] = { catalog, ui }.
 *   PRICES + VARIANT IDS come from that catalog (built by Liquid from product objects) — never typed
 *   here. The pure kitting math (clamp rounding, BOM, multi-line payload) is the shared
 *   window.EZSnap.createHitchEngine (assets/ez-engine-hitch.js — already behaviour-gated).
 *
 * PERSISTENCE: the current build is mirrored to the URL (?c&st&cl&bg&ve) — shareable/restores on load.
 * CART: a single multi-line POST to /cart/add.js (Shopify AJAX), then → /cart.
 * ============================================================================ */
(function () {
  "use strict";

  function money(n) {
    n = Number(n) || 0;
    // non-store currency (Canada market = CAD): the ONE theme formatter, window.EZ (layout/theme.liquid)
    var E = window.EZ;
    if (E && E.isBase === false && typeof E.money === "function") return E.money(n);
    var opts = { minimumFractionDigits: n % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 };
    return "$" + n.toLocaleString("en-US", opts);
  }
  // Free-shipping threshold label: "$225" (USD) / "US$225" (rates are USD, converted at checkout).
  function fsLabel() {
    var E = window.EZ;
    return (E && typeof E.fs === "string" && /\d/.test(E.fs)) ? E.fs : "$225";
  }
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"]/g, function (m) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[m];
    });
  }
  function el(tag, cls, html) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html != null) e.innerHTML = html;
    return e;
  }

  // =========================================================================
  // One island instance.
  // =========================================================================
  function HitchConfigurator(root, cfg) {
    var CAT = cfg.catalog;
    var UI = cfg.ui || {};
    var STEP_IMG = cfg.stepImages || null;

    var stepsEl = root.querySelector("[data-ez-steps]");
    var railTotal = root.querySelector("[data-ez-rail-total]");
    var railLabel = root.querySelector("[data-ez-price-label]");
    var cardSub = root.querySelector("[data-ez-card-sub]");
    var stepImg = root.querySelector(".ez-hcfg__step-img, [data-ez-step-img]");
    var noscriptLink = root.querySelector("[data-ez-kit-url]");

    function colorName(code) { var c = CAT.colors.find(function (x) { return x.code === code; }); return c ? c.name : ""; }
    // Kit price for a (color, connector). Each colour carries wc{} + nc{} sides (legacy top-level = WC).
    // Pure (no st) so it can run before state exists (engine bootstrap).
    function kitPriceFor(colorCode, conn) {
      var c = colorCode ? CAT.colors.find(function (x) { return x.code === colorCode; }) : CAT.colors[0];
      if (!c) return CAT.kitPrice;
      var side = conn === "NC" ? c.nc : c.wc;
      if (side && side.price != null) return side.price;
      return c.price != null ? c.price : CAT.kitPrice;
    }
    function kitPrice() { return kitPriceFor(st.color, st.connector); }
    function recRaw() { return st.connector === "NC" ? (CAT.recClampRawNc != null ? CAT.recClampRawNc : 13) : CAT.recClampRaw; }
    function addonBySku(sku) { return CAT.addons[sku] || null; }

    // ── stud color upgrade (display) ──
    // The studs ship INSIDE the kit's snap pack (EZ-C-SNA-{snapCount}-{panel}-{stud}). Matching the panel is
    // included; a different stud color is ONE flat upgrade per build (EZKitSnaps.UPGRADE, $60). The server
    // prices it on the snap line; here it is display only (a studFee line that is never posted).
    function upgradeUsd() { var K = window.EZKitSnaps; return (K && Number(K.UPGRADE)) || 60; }
    // A flat USD amount: a non-USD shopper (Canada market) sees "US$60" (typed-USD rule, window.EZ.usd) and it
    // stays OUT of the presentment-currency running total rather than mixing currencies in one sum.
    function isBaseCcy() { var E = window.EZ; return !(E && E.isBase === false); }
    function upgradeAmt() { return isBaseCcy() ? upgradeUsd() : 0; }
    function upgradeText() {
      var E = window.EZ;
      return (!isBaseCcy() && E && typeof E.usd === "function") ? E.usd("$" + upgradeUsd()) : money(upgradeUsd());
    }

    var clampPrice = {}, velPrice = {};
    CAT.clampPacks.forEach(function (n) { var a = addonBySku("EZ-B-CMP-" + n); if (a) clampPrice[n] = a.price; });
    [10, 20].forEach(function (ft) { var a = addonBySku("EZ-C-VEL-" + ft); if (a) velPrice[ft] = a.price; });
    var bagAddon = CAT.bagSku ? addonBySku(CAT.bagSku) : null;

    var engine = window.EZSnap.createHitchEngine({
      price: kitPriceFor(null, "WC"),
      panelSize: CAT.panelSize,
      snapCount: CAT.snapCount,
      inclVelFt: CAT.inclVelFt,
      recClampRaw: CAT.recClampRaw,
      bagSku: CAT.bagSku || "EZ-C-SKB-X",
      bagPrice: bagAddon ? bagAddon.price : 0,
      bagLabel: CAT.bagLabel || "Hitch 74″",
      colors: CAT.colors.map(function (c) { return { code: c.code, name: c.name }; }),
      clampPacks: CAT.clampPacks,
      clampPrice: clampPrice,
      velPrice: velPrice,
      variantId: {
        kit: function (c, conn) { var x = CAT.colors.find(function (o) { return o.code === c; }); if (!x) return null; var side = conn === "NC" ? x.nc : x.wc; return (side && side.variantId) ? side.variantId : x.variantId; },
        clamp: function (n) { var a = addonBySku("EZ-B-CMP-" + n); return a ? a.variantId : null; },
        vel: function (ft) { var a = addonBySku("EZ-C-VEL-" + ft); return a ? a.variantId : null; },
        bag: function () { return bagAddon ? bagAddon.variantId : null; }
      }
    });

    // ── state ──
    var st = {
      open: 1, connector: "WC", color: null, studMode: "match", studColor: null,
      clampsOn: true, clampPack: engine.recClampPack(),
      bagOn: false, velcroOn: false, velFt: 10, reviewIncl: false
    };

    function studsBilled() { return st.studMode === "diff" && st.studColor && st.studColor !== st.color; }
    // the stud color the snap pack ships with: the picked different color, else the panel color (included)
    function studCode() { return studsBilled() ? st.studColor : st.color; }
    // The kit's snaps: caps always match the enclosure; the studs match it too unless the customer picked a
    // different stud color (a MAIN choice in the Stud color step, not an add-on). Says WHAT the snaps match
    // (Troy's question was "color-matched to what?").
    function snapWording() {
      if (studsBilled()) return colorName(st.color) + " caps color-matched to your enclosure with " + colorName(st.studColor) + " studs";
      return colorName(st.color) + " snaps color-matched to your enclosure";
    }
    // Optional extras only (clamps, bag, Velcro). The stud color upgrade is a main choice, added in total().
    function addonTotal() {
      if (!st.color) return 0;
      var t = 0;
      if (st.clampsOn) t += (clampPrice[st.clampPack] || 0);
      if (st.bagOn && bagAddon) t += bagAddon.price;
      if (st.velcroOn) t += (velPrice[st.velFt] || 0);
      return t;
    }
    function total() { return kitPrice() + (st.color ? addonTotal() + (studsBilled() ? upgradeAmt() : 0) : 0); }
    function lines() {
      var out = engine.buildHitchLines({
        color: st.color, connector: st.connector, kitPrice: kitPrice(),
        studColor: studCode(),   // snap pack: caps = panel color, studs = this
        clampsOn: st.clampsOn, clampPack: st.clampPack,
        bagOn: st.bagOn, velcroOn: st.velcroOn, velFt: st.velFt
      });
      // Stud color upgrade: ONE display line per build, right after the snap pack. Never posted (the server
      // charges it on the snap line); it is here so the review and total show it.
      if (studsBilled()) {
        var at = 0;
        out.forEach(function (l, i) { if (l.kitSnaps) at = i + 1; });
        out.splice(at || out.length, 0, {
          sku: "", title: "Stud color upgrade", opts: colorName(st.studColor) + " studs · one charge for the whole build",
          qty: 1, price: upgradeAmt(), studFee: true
        });
      }
      return out;
    }

    // ── URL persistence ──
    function readUrl() {
      var sp = new URLSearchParams(window.location.search);
      // A shared/emailed ?c=… link wins; otherwise fall back to the saved build so it
      // survives a refresh AND a browser close (localStorage, not the URL alone).
      if (!sp.get("c")) {
        var saved = null;
        try { saved = localStorage.getItem("ezsnap_hitch"); } catch (e) {}
        if (saved) sp = new URLSearchParams(saved);
      }
      if (sp.get("cn") === "nc") st.connector = "NC";
      var c = sp.get("c");
      if (c && CAT.colors.find(function (x) { return x.code === c; })) { st.color = c; st.open = 6; }
      var stC = sp.get("st");
      if (stC && st.color && stC !== c && CAT.colors.find(function (x) { return x.code === stC; })) { st.studMode = "diff"; st.studColor = stC; }
      var cl = sp.get("cl");
      if (cl === "0") st.clampsOn = false;
      else if (cl && CAT.clampPacks.indexOf(Number(cl)) >= 0) { st.clampsOn = true; st.clampPack = Number(cl); }
      if (sp.get("bg") === "1") st.bagOn = true;
      var ve = sp.get("ve"); if (ve && velPrice[Number(ve)]) { st.velcroOn = true; st.velFt = Number(ve); }
    }
    function writeUrl() {
      if (!st.color) return;
      var p = new URLSearchParams();
      p.set("c", st.color);
      if (st.connector === "NC") p.set("cn", "nc");
      if (studsBilled()) p.set("st", st.studColor);
      p.set("cl", st.clampsOn ? String(st.clampPack) : "0");
      if (st.bagOn) p.set("bg", "1");
      if (st.velcroOn) p.set("ve", String(st.velFt));
      // Persist the same build to localStorage so it also survives a browser close,
      // not just a refresh (the URL mirror covers refresh + shareable links).
      try { localStorage.setItem("ezsnap_hitch", p.toString()); } catch (e) {}
      window.history.replaceState(null, "", window.location.pathname + "?" + p.toString());
    }

    // ── step image ──
    function stepImgSrc() {
      if (!STEP_IMG) return null;
      if (!st.color) return STEP_IMG.start || null;
      if (st.open <= 3) return STEP_IMG.color || STEP_IMG.start || null;
      if (st.open === 4) return STEP_IMG.clamps || STEP_IMG.color || null;
      return STEP_IMG.review || STEP_IMG.clamps || null;
    }

    // ── paint ──
    function paint() {
      if (railTotal) railTotal.textContent = st.color ? money(total()) : money(kitPrice());
      if (railLabel) railLabel.textContent = st.color ? (addonTotal() > 0 ? (UI.railKitAddons || "Your kit + add-ons") : (UI.railKit || "Your kit")) : (UI.priceLabel || "Your kit price");
      if (cardSub) cardSub.textContent = [CAT.panelSize + " zipper door", st.color ? colorName(st.color) : "", (st.connector === "NC" ? "No connector" : "With connector")].filter(Boolean).join(" · ") || (UI.cardSub || "Configure yours below");
      var src = stepImgSrc();
      if (stepImg && src) stepImg.setAttribute("src", src);
      renderSteps();
      writeUrl();
    }

    // ── atoms ──
    function stepHeader(n, title, done, locked, chip) {
      var h = el("div", "ez-hcfg__step-head" + (locked ? " is-locked" : ""));
      var badge = el("span", "ez-hcfg__step-badge" + (done ? " is-done" : (st.open === n ? " is-open" : "")));
      badge.textContent = done ? "✓" : String(n);
      h.appendChild(badge);
      h.appendChild(el("span", "ez-hcfg__step-title", esc(title)));
      if (locked) {
        h.appendChild(el("span", "ez-hcfg__step-lock", '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="2.5" y="6" width="9" height="6" rx="1" stroke-width="1.2"/><path d="M4.5 6V4.5a2.5 2.5 0 015 0V6" stroke-width="1.2"/></svg>'));
      } else if (done && chip && st.open !== n) {
        h.appendChild(el("span", "ez-hcfg__step-chip", esc(chip) + " <b>edit</b>"));
      }
      if (!locked) h.addEventListener("click", function () { if (st.open !== n) { st.open = n; paint(); } });
      return h;
    }

    // subLabel (optional): code -> a short second line under the name (the stud picker's "Included" / "+$60").
    function colorSwatchRow(selected, onPick, disabledCode, small, subLabel) {
      var wrap = el("div", "ez-hcfg__swatch-row" + (small ? " is-small" : ""));
      CAT.colors.forEach(function (c) {
        var disabled = disabledCode && c.code === disabledCode;
        var sub = subLabel ? subLabel(c.code) : "";
        var b = el("div", "ez-hcfg__swatch" + (selected === c.code ? " is-sel" : "") + (disabled ? " is-disabled" : ""));
        b.setAttribute("role", "button"); b.setAttribute("tabindex", disabled ? "-1" : "0"); b.setAttribute("aria-label", c.name + (sub ? ", " + sub : ""));
        var dot = el("span", "ez-hcfg__swatch-dot");
        dot.style.background = c.hex;
        b.appendChild(dot);
        b.appendChild(el("span", "ez-hcfg__swatch-name", esc(disabled ? "in kit" : c.name) + (sub ? '<br><small class="ez-hcfg__stud-tag' + (/^\+/.test(sub) ? " is-upgrade" : "") + '">' + esc(sub) + "</small>" : "")));
        if (!disabled) {
          b.addEventListener("click", function () { onPick(c.code); });
          b.addEventListener("keydown", function (e) { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPick(c.code); } });
        }
        wrap.appendChild(b);
      });
      return wrap;
    }

    function ctaBtn(label, onClick, dark, disabled) {
      var b = el("button", "ez-hcfg__cta" + (dark ? " is-dark" : ""), esc(label));
      b.type = "button";
      b.disabled = !!disabled;
      if (!disabled) b.addEventListener("click", onClick);
      return b;
    }

    function addonRow(opts) {
      var row = el("div", "ez-hcfg__addon" + (opts.on ? " is-on" : ""));
      var top = el("div", "ez-hcfg__addon-top");
      var box = el("span", "ez-hcfg__addon-box" + (opts.on ? " is-on" : ""));
      if (opts.on) box.innerHTML = '<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6.2l2.2 2.2 4.8-4.8" stroke="#fff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      var mid = el("div", "ez-hcfg__addon-mid");
      var titleRow = el("div", "ez-hcfg__addon-titlerow");
      titleRow.appendChild(el("span", "ez-hcfg__addon-title", esc(opts.title)));
      if (opts.rec) titleRow.appendChild(el("span", "ez-hcfg__addon-tag is-rec", "Recommended"));
      if (opts.extra) titleRow.appendChild(el("span", "ez-hcfg__addon-tag", "Extra"));
      mid.appendChild(titleRow);
      mid.appendChild(el("div", "ez-hcfg__addon-sub", esc(opts.sub)));
      if (opts.extraChildren) mid.appendChild(opts.extraChildren);
      var left = el("div", "ez-hcfg__addon-left");
      left.appendChild(box); left.appendChild(mid);
      top.appendChild(left);
      top.appendChild(el("span", "ez-hcfg__addon-price", esc(opts.price)));
      row.appendChild(top);
      row.addEventListener("click", function (e) { if (e.target.closest("[data-ez-stop]")) return; opts.toggle(); });
      return row;
    }

    function packRow(packs, current, onPick, recPack) {
      var wrap = el("div", "ez-hcfg__packs");
      wrap.setAttribute("data-ez-stop", "");
      packs.forEach(function (n) {
        var p = el("div", "ez-hcfg__pack" + (current === n ? " is-sel" : ""), esc(String(n)) + (n === recPack ? " ★" : ""));
        p.addEventListener("click", function (e) { e.stopPropagation(); onPick(n); });
        wrap.appendChild(p);
      });
      return wrap;
    }

    // the kit BOM in the shopper's stud pick: "100 Black snaps", or "100 snaps (Black caps, White studs)"
    function bomItems(color) {
      return engine.hitchBOM(color, st.connector).map(function (item) {
        if (studsBilled() && st.studColor !== color && item === CAT.snapCount + " " + colorName(color) + " snaps") {
          return CAT.snapCount + " snaps (" + colorName(color) + " caps, " + colorName(st.studColor) + " studs)";
        }
        return item;
      });
    }
    function bomList(color) {
      var ul = el("ul", "ez-hcfg__bom");
      bomItems(color).forEach(function (item) {
        var li = el("li", "ez-hcfg__bom-item");
        li.innerHTML = '<span class="ez-hcfg__bom-node" aria-hidden="true"><svg width="8" height="8" viewBox="0 0 8 8" fill="none"><circle cx="4" cy="4" r="3.5" stroke="var(--ez-copper,#c4622a)" stroke-width="0.75" fill="none"/><circle cx="4" cy="4" r="1.5" fill="var(--ez-copper,#c4622a)"/></svg></span><span>' + esc(item) + "</span>";
        ul.appendChild(li);
      });
      return ul;
    }

    function stepBody(n) {
      var body = el("div", "ez-hcfg__step-body");
      var resolved = !!st.color;

      if (n === 1) {
        // Connector choice. With = the 10 ft fabric that seals the BACK of the enclosure (needed when the rig has
        // no EZ-Snap side skirting there, or runs AirSkirts) so wind cannot parachute in behind the panel. No =
        // the customer already runs EZ-Snap side skirting on the back, which closes that gap; skip it and save.
        var cg = el("div", "ez-hcfg__opt-grid");
        var cWc = el("div", "ez-hcfg__opt-card" + (st.connector === "WC" ? " is-sel" : ""));
        cWc.innerHTML = (UI.connectorWcImg ? '<img class="ez-hcfg__opt-img" src="' + esc(UI.connectorWcImg) + '" alt="Hitch enclosure with the 10 ft connector fabric sealing the back, no side skirting on the back" loading="lazy">' : '') + '<div class="ez-hcfg__opt-title">With connector</div><div class="ez-hcfg__opt-sub">Seals the back of the hitch enclosure so wind cannot get behind the panel. Choose this if you run the hitch kit without EZ-Snap side skirting: standalone, or with AirSkirts on the sides.</div><div class="ez-hcfg__opt-price">' + money(kitPriceFor(st.color, "WC")) + "</div>";
        cWc.addEventListener("click", function () { st.connector = "WC"; paint(); });
        var cNc = el("div", "ez-hcfg__opt-card" + (st.connector === "NC" ? " is-sel" : ""));
        cNc.innerHTML = (UI.connectorNcImg ? '<img class="ez-hcfg__opt-img" src="' + esc(UI.connectorNcImg) + '" alt="Hitch enclosure on the front with EZ-Snap side skirting on the back closing the gap" loading="lazy">' : '') + '<div class="ez-hcfg__opt-title">No connector</div><div class="ez-hcfg__opt-sub">Choose this if you already run EZ-Snap side skirting on the back. Your skirting closes that gap, so you can skip the connector and save ' + money(kitPriceFor(st.color, "WC") - kitPriceFor(st.color, "NC")) + '.</div><div class="ez-hcfg__opt-price">' + money(kitPriceFor(st.color, "NC")) + "</div>";
        cNc.addEventListener("click", function () { st.connector = "NC"; paint(); });
        cg.appendChild(cWc); cg.appendChild(cNc);
        body.appendChild(cg);
        body.appendChild(el("p", "ez-hcfg__step-sub", "Why it matters: if the back of the enclosure isn't sealed, wind gets behind the panel like a parachute and can pull the snaps off. Already running EZ-Snap side skirting on the back? Pick No connector."));
        body.appendChild(ctaBtn("Continue to enclosure color →", function () { st.open = 2; paint(); }));
      }

      else if (n === 2) {
        // no auto-advance on color pick — the visible Continue button below drives the step forward
        body.appendChild(colorSwatchRow(st.color, function (code) {
          st.color = code;
          // studs follow the panel (included) unless a different stud color was picked; a pick that now
          // equals the new panel color collapses back to the included match
          if (st.studMode === "diff" && st.studColor === code) st.studMode = "match";
          paint();
        }));
        var p1 = el("p", "ez-hcfg__step-note");
        var fabClause = st.connector === "NC" ? "" : " the 10 ft connector fabric (the strip that seals the back of the enclosure),";
        p1.innerHTML = "<b>" + money(kitPrice()) + ": one price, any color.</b> Your presewn panel," + fabClause + " and the " + CAT.snapCount + " snaps &amp; caps all ship in this color.";
        body.appendChild(p1);
        body.appendChild(el("p", "ez-hcfg__step-sub", "Studs and caps match this panel color by default. Pick a different stud color next if you’d like them to blend with another surface, like your RV body or trim, instead."));
        // always visible so the path forward reads at a glance (older customers); disabled until a color is picked
        body.appendChild(ctaBtn(UI.step2Cta || "Continue to stud color →", function () { st.open = 3; paint(); }, false, !resolved));
      }

      else if (n === 3 && resolved) {
        // Stud color: a MAIN choice right after the panel color. One picker; the panel color is the selected
        // default ("Included"), any other color is the one flat stud color upgrade for the build.
        var upTxt = "+" + upgradeText();
        var p2 = el("p", "ez-hcfg__step-note");
        p2.innerHTML = "A stud is the adhesive base that stays on your RV year-round. Your kit's " + CAT.snapCount + " studs match your <b>" + esc(colorName(st.color)) + "</b> panel by default. Want them to blend with your RV body or trim instead? Pick that color.";
        body.appendChild(p2);
        body.appendChild(colorSwatchRow(studCode(), function (code) {
          if (code === st.color) st.studMode = "match";
          else { st.studMode = "diff"; st.studColor = code; }
          paint();
        }, null, false, function (code) { return code === st.color ? "Included" : upTxt; }));
        body.appendChild(el("p", "ez-hcfg__step-sub", studsBilled()
          ? '<span class="ez-stud-pill is-upgrade">' + esc(upTxt) + '</span> <b>Stud color upgrade: ' + esc(colorName(st.studColor)) + " studs.</b> One flat " + esc(upTxt) + " for the whole build, not per snap. Caps stay " + esc(colorName(st.color)) + " to match the panel. Pick " + esc(colorName(st.color)) + " again for included studs."
          : '<span class="ez-stud-pill is-included">Included</span> <b>' + esc(colorName(st.color)) + " studs</b> match your " + esc(colorName(st.color)) + " panel. No extra cost. Your " + CAT.snapCount + " studs and caps ship in " + esc(colorName(st.color)) + "."));
        body.appendChild(ctaBtn(UI.step3Cta || "Continue to pipe clamps →", function () { st.open = 4; paint(); }));
      }

      else if (n === 4 && resolved) {
        var recPack = engine.recClampPack(recRaw());
        var clampChildren = null;
        if (st.clampsOn) {
          clampChildren = el("div", "ez-hcfg__pack-wrap");
          clampChildren.setAttribute("data-ez-stop", "");
          clampChildren.appendChild(el("span", "ez-hcfg__pack-label", "Pack:"));
          clampChildren.appendChild(packRow(CAT.clampPacks, st.clampPack, function (nn) { st.clampPack = nn; paint(); }, recPack));
        }
        body.appendChild(addonRow({
          on: st.clampsOn, toggle: function () { st.clampsOn = !st.clampsOn; paint(); },
          title: UI.clampLabel || "Pipe clamps",
          price: money(clampPrice[st.clampPack] || 0), rec: true,
          sub: (UI.clampSub || "") + " Recommended: " + recPack + "-count pack.",
          extraChildren: clampChildren
        }));
        body.appendChild(ctaBtn(UI.step4Cta || "Continue to add-ons →", function () { st.open = 5; paint(); }));
      }

      else if (n === 5 && resolved) {
        var recap = el("div", "ez-hcfg__recap");
        var rHead = el("div", "ez-hcfg__recap-head");
        rHead.innerHTML = '<span class="ez-hcfg__recap-name">Hitch Enclosure Kit</span><span class="ez-hcfg__recap-sku">' + esc(engine.hitchSku(st.color, st.connector)) + "</span>";
        recap.appendChild(rHead);
        recap.appendChild(bomList(st.color));
        recap.appendChild(el("div", "ez-hcfg__recap-foot", "Installs with the same no-drill 3M™ VHB snap system as our RV skirting: no holes in your rig."));
        body.appendChild(recap);

        var xh = el("div", "ez-hcfg__extras-head");
        var fabInc = st.connector === "NC" ? "" : ", and the connector fabric";
        xh.innerHTML = '<div class="ez-hcfg__extras-title">Optional extras</div><div class="ez-hcfg__extras-sub">Your kit already ships complete: ' + esc(snapWording()) + ", one Gecko Velcro™ strip" + fabInc + " included. Everything below is an optional add-on, not a replacement.</div>";
        body.appendChild(xh);

        if (bagAddon) {
          body.appendChild(addonRow({
            on: st.bagOn, toggle: function () { st.bagOn = !st.bagOn; paint(); },
            title: UI.bagTitle || "Storage bag", extra: true,
            price: money(bagAddon.price),
            sub: "Hitch enclosure storage bag (74″): folds the panel away for the season."
          }));
        }
        var velChildren = null;
        if (st.velcroOn) {
          velChildren = el("div", "ez-hcfg__packs"); velChildren.setAttribute("data-ez-stop", "");
          [10, 20].forEach(function (ft) {
            if (velPrice[ft] == null) return;
            var p = el("div", "ez-hcfg__pack" + (st.velFt === ft ? " is-sel" : ""), ft + " ft");
            p.addEventListener("click", function (e) { e.stopPropagation(); st.velFt = ft; paint(); });
            velChildren.appendChild(p);
          });
        }
        if (velPrice[st.velFt] != null) {
          body.appendChild(addonRow({
            on: st.velcroOn, toggle: function () { st.velcroOn = !st.velcroOn; paint(); },
            title: "Extra Gecko Velcro™", extra: true,
            price: money(velPrice[st.velFt] || 0),
            sub: "Your kit already includes a " + CAT.inclVelFt + " ft strip. Add more for extra access points or reorder.",
            extraChildren: velChildren
          }));
        }
        body.appendChild(ctaBtn(UI.step5Cta || "Continue to review →", function () { st.open = 6; paint(); }, true));
      }

      else if (n === 6 && resolved) {
        var ls = lines();
        var wrap = el("div", "ez-hcfg__review");
        ls.forEach(function (l) {
          var isKit = l.sku.indexOf("EZ-B-HIT") === 0;
          // key for Edit/Remove: the stud upgrade (display line, no SKU) keys as "stud-upgrade"
          var key = l.studFee ? "stud-upgrade" : l.sku;
          var rr = el("div", "ez-hcfg__review-row" + (isKit ? " is-kit" : ""));
          // the snap pack is included with the kit: Edit (stud color) only, no Remove
          var act = isKit ? '<span class="ez-hcfg__review-incl" data-incl>What’s included ' + (st.reviewIncl ? "▾" : "▸") + "</span>"
            : (l.kitSnaps ? "" : '<span class="ez-hcfg__review-remove" data-remove="' + esc(key) + '">Remove</span>');
          var meta = '<div class="ez-hcfg__review-meta"><div class="ez-hcfg__review-title">' + esc(l.title) + "</div>" +
            (l.opts ? '<div class="ez-hcfg__review-opts">' + esc(l.opts) + "</div>" : "") +
            (l.sku ? '<div class="ez-hcfg__review-sku">' + esc(l.sku) + "</div>" : "") + '<div class="ez-hcfg__review-actions">' +
            '<span class="ez-hcfg__review-edit" data-edit="' + esc(key) + '">Edit</span>' + act +
            "</div></div>";
          var amt = l.kitSnaps ? "Included" : (l.studFee ? upgradeText() : money(l.price * l.qty));
          var price = '<div class="ez-hcfg__review-pricewrap"><div class="ez-hcfg__review-price">' + esc(amt) + "</div>" +
            (l.qty > 1 && !l.kitSnaps && !l.studFee ? '<div class="ez-hcfg__review-qty">Qty ' + l.qty + " · " + money(l.price) + " ea</div>" : "") + "</div>";
          rr.innerHTML = '<div class="ez-hcfg__review-line">' + meta + price + "</div>";
          if (isKit && st.reviewIncl) rr.appendChild(bomList(st.color));
          wrap.appendChild(rr);
        });
        body.appendChild(wrap);

        var totalRow = el("div", "ez-hcfg__total");
        totalRow.innerHTML = '<span class="ez-hcfg__total-label">Total</span><span class="ez-hcfg__total-val">' + money(total()) + "</span>";
        body.appendChild(totalRow);

        var atc = ctaBtn((UI.atcLabel || "Add to cart") + " →", addToCart);
        atc.classList.add("ez-hcfg__atc");
        body.appendChild(atc);
        var re = el("div", "ez-hcfg__reassure", "Free shipping " + fsLabel() + "+ · Limited Lifetime Warranty");
        body.appendChild(re);
        if (st.atcError) {
          var errEl = el("div", "ez-hcfg__atc-error");
          errEl.setAttribute("role", "alert");
          errEl.textContent = st.atcError;
          errEl.style.cssText = "margin-top:10px;padding:10px 12px;border-radius:8px;background:rgba(196,98,42,.09);color:#8a2b12;font-size:13px;line-height:1.45;";
          body.appendChild(errEl);
        }

        body.querySelectorAll("[data-edit]").forEach(function (n2) {
          n2.addEventListener("click", function () {
            var sku = n2.getAttribute("data-edit");
            st.open = sku.indexOf("EZ-B-HIT") === 0 ? 2 : (sku === "stud-upgrade" || sku.indexOf("EZ-C-SNA") === 0) ? 3 : sku.indexOf("EZ-B-CMP") === 0 ? 4 : 5;
            paint();
          });
        });
        var incl = body.querySelector("[data-incl]"); if (incl) incl.addEventListener("click", function () { st.reviewIncl = !st.reviewIncl; paint(); });
        body.querySelectorAll("[data-remove]").forEach(function (n2) {
          n2.addEventListener("click", function () {
            var sku = n2.getAttribute("data-remove");
            if (sku === "stud-upgrade") st.studMode = "match";   // drop the upgrade = back to panel-matched studs
            else if (sku.indexOf("EZ-B-CMP") === 0) st.clampsOn = false;
            else if (sku.indexOf("EZ-C-VEL") === 0) st.velcroOn = false;
            else if (sku === CAT.bagSku) st.bagOn = false;
            paint();
          });
        });
      }
      return body;
    }

    function peekBody(n, label) {
      var peek = el("div", "ez-hcfg__peek");
      var ghost = el("div", "ez-hcfg__peek-ghost");
      ghost.setAttribute("aria-hidden", "true");
      ghost.appendChild(stepBody(n));
      peek.appendChild(ghost);
      // No floating CTA on the ghost — the visible advance button lives at the bottom of the active step.
      // The ghost stays click-to-advance via an invisible full-area overlay.
      var hit = el("button", "ez-hcfg__peek-hit");
      hit.type = "button";
      hit.setAttribute("aria-label", label || (UI.peekCtas && UI.peekCtas[n]) || "Continue");
      hit.addEventListener("click", function () { st.open = n; paint(); });
      peek.appendChild(hit);
      return peek;
    }

    function renderSteps() {
      if (!stepsEl) return;
      stepsEl.innerHTML = "";
      var resolved = !!st.color;
      var titles = [UI.connectorLabel || "Connector", UI.colorLabel || "Enclosure color", UI.studLabel || "Stud color", UI.clampLabel || "Pipe clamps", UI.addonLabel || "Add-ons", UI.reviewLabel || "Review"];
      var chips = [
        st.connector === "NC" ? "No connector" : "With connector",
        st.color ? colorName(st.color) : null,
        resolved ? (studsBilled() ? colorName(st.studColor) + " studs" : "Matches panel") : null,
        resolved ? (st.clampsOn ? st.clampPack + "-count" : "None") : null,
        null, null
      ];
      var dones = [st.open > 1, resolved, resolved && (st.studMode === "match" || studsBilled()), resolved && st.open > 4, resolved && st.open > 5, false];
      for (var n = 1; n <= 6; n++) {
        var locked = n > 2 && !resolved;
        var isPeek = st.open !== n && n === st.open + 1;
        var wrap = el("div", "ez-hcfg__step" + (n < 6 ? "" : " is-last") + (locked ? " is-locked" : "") + (isPeek ? " is-peek" : ""));
        wrap.appendChild(stepHeader(n, titles[n - 1], dones[n - 1], locked, chips[n - 1]));
        if (st.open === n && !locked) wrap.appendChild(stepBody(n));
        else if (isPeek && !locked) wrap.appendChild(peekBody(n, "Continue to " + (titles[n - 1] || "").toLowerCase()));
        stepsEl.appendChild(wrap);
      }
    }

    // ── multi-line add to cart ──
    var atcBusy = false; // re-entrancy guard: block a second POST while one is in flight (double-tap dupes)
    function addToCart() {
      if (!st.color) return;
      if (atcBusy) return;                 // ignore re-entrant clicks (double-tap) while a POST is in flight
      var bid = 'b' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
      var bprops = { _build: bid, _build_kind: 'hitch', _build_label: 'Hitch enclosure' };
      var allLines = lines();
      if (!allLines.length) return;
      // Visible "Kit includes" on the bundled hitch KIT line (EZ-B-HIT) — surfaces the enclosure BOM at
      // checkout / order email / admin, the SAME sourced list the on-page BOM shows (engine.hitchBOM), in the
      // selected enclosure color. Add-on lines (snap pack/clamps/velcro/bag) keep only the hidden _build tags.
      var kitIncludes = bomItems(st.color).join(' · ');
      // The snap pack is a display line (no variantId): it becomes real cart lines via window.EZKitSnaps below,
      // held as a null slot right where it sits in the build. The stud upgrade line is display only (the
      // server charges it on the snap line) and is never posted.
      var postLines = allLines.filter(function (l) { return !l.studFee; });
      var snapLines = postLines.filter(function (l) { return l.kitSnaps; });
      var kitLine = postLines.filter(function (l) { return l.sku && l.sku.indexOf('EZ-B-HIT') === 0; })[0];
      var items = postLines.map(function (l) {
        if (l.kitSnaps) return null;
        var props = (l.sku && l.sku.indexOf('EZ-B-HIT') === 0) ? Object.assign({}, bprops, { 'Kit includes': kitIncludes }) : bprops;
        return { id: l.variantId, quantity: l.qty || 1, properties: props };
      });
      var unavailable = "One item in your build is temporarily unavailable, so nothing was added. We won't ship a partial order. Please refresh, or contact us and we'll place it for you.";
      // Cart integrity: never POST a PARTIAL build. If any line the shopper was shown (and charged for)
      // has no variant — e.g. an add-on whose product isn't wired into addon_products — or the kit has no
      // snap pack to add, block the add and surface it, rather than silently shipping a bare kit.
      var missing = items.some(function (it) { return it ? !it.id : false; });
      if (missing || !kitLine || !snapLines.length) {
        st.atcError = unavailable;
        paint();
        return;
      }
      st.atcError = null;
      atcBusy = true;
      var btn = root.querySelector(".ez-hcfg__atc"); if (btn) { btn.disabled = true; btn.style.opacity = "0.6"; }
      function done() { atcBusy = false; var b = root.querySelector(".ez-hcfg__atc"); if (b) { b.disabled = false; b.style.opacity = ""; } }
      // All-or-nothing: resolve the snap-pack variants FIRST. Snap lines carry only the build's grouping props
      // (bprops; the helper adds _kit_snaps), never the visible "Kit includes". If the helper is missing or a
      // pack variant can't be found, show the build error and POST nothing.
      var K = window.EZKitSnaps;
      var snapsReady = Promise.resolve().then(function () {   // a sync throw also lands in the reject handler
        if (!K || typeof K.lines !== "function") throw new Error("kit snaps unavailable");
        return K.lines({
          packs: snapLines.map(function (l) { return { n: l.packN, qty: l.qty }; }),
          cap: snapLines[0].cap, stud: snapLines[0].stud, kitQty: kitLine.qty || 1, props: bprops
        });
      }).then(function (got) { if (!got || !got.length) throw new Error("kit snaps: no pack lines"); return got; });
      snapsReady.then(function (got) {
        var payload = [];
        items.forEach(function (it, i) {
          if (it) payload.push(it);
          else if (postLines[i] === snapLines[0]) got.forEach(function (s) { payload.push({ id: s.id, quantity: s.quantity, properties: s.properties }); });
        });
        return fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify({ items: payload })
        }).then(function (r) {
          if (!r.ok) throw new Error("add failed");
          return r.json();
        }).then(function (data) {
          done();
          // Drawer by default; hard /cart redirect only when the section opts out.
          if (root.getAttribute("data-cart-redirect") === "true") { window.location.href = "/cart"; return; }
          document.dispatchEvent(new CustomEvent("ez:cart:added", { detail: { cart: data } }));
        }).catch(function () { done(); window.location.href = noscriptLink ? noscriptLink.getAttribute("href") : "/cart"; });
      }, function () {
        // snap packs could not resolve: nothing was posted. Same partial-build error as a missing variant.
        done();
        st.atcError = unavailable;
        paint();
      });
    }

    // ── modal ──
    var modal = root.querySelector("[data-ez-modal]");
    root.querySelectorAll("[data-ez-open-modal]").forEach(function (b) { b.addEventListener("click", function () { if (modal) modal.hidden = false; }); });
    root.querySelectorAll("[data-ez-close-modal]").forEach(function (b) { b.addEventListener("click", function () { if (modal) modal.hidden = true; }); });

    // ── boot ──
    readUrl();
    st.clampPack = st.clampPack || engine.recClampPack();
    paint();
  }

  // =========================================================================
  // Mount every instance once the shared engine is present.
  // =========================================================================
  function mountAll() {
    if (!window.EZSnap || !window.EZSnap.createHitchEngine) return setTimeout(mountAll, 40);
    var cfgs = window.__EZ_HITCH_CFG__ || {};
    Object.keys(cfgs).forEach(function (sid) {
      var cfg = cfgs[sid];
      if (!cfg || cfg.__mounted) return;
      var root = document.getElementById("ez-configurator-hitch-" + sid);
      if (!root) return;
      if (!cfg.catalog || !cfg.catalog.colors || !cfg.catalog.colors.length) return;
      cfg.__mounted = true;
      new HitchConfigurator(root, cfg);
    });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", mountAll);
  else mountAll();
})();
